import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { INVESTMENT_HORIZON_FOR_PK } from 'constants/constants';
import {
    useCreatePortfolioActions,
    useCreatePortfolioSelectors,
    useCreatePortfolioState,
} from 'datasource/CreatePortfolio';
import { usePortfolioProcessNavigation } from 'hooks/helpers/usePortfolioProcessNavigation';
import { adaptModels, adaptStrategies } from 'pages/ClientOverview/pages/Portfolios/adapters/adaptModelPortfolio';
import {
    useBaseAgentInformation,
} from 'pages/ClientOverview/pages/Portfolios/components/AgentInformation/hooks/useBaseAgentInformation';
import SM from 'services/ServiceManager';
import { notification } from 'ui-library';
import { STRATEGY_CHANGED } from 'constants/sessionStorageKeys';

export const useAgentInformation = ({
    dfsClientId, saveFeeData, isPk,
}) => {
    const [pageErrors, setPageErrors] = useState(undefined);
    const [isPreloadInProgress, setIsPreloadInProgress] = useState(true);
    const [continueInProgress, setContinueInProgress] = useState(false);
    const { i18n: { language } } = useTranslation();
    const {
        selectedProductSelector,
        currentInvestmentApplicationSelector,
        is3bProductGroupSelector,
    } = useCreatePortfolioSelectors();

    const {
        saveInvestmentApplicationDataAction,
    } = useCreatePortfolioActions();
    const {
        currentInvestmentApplicationId,
    } = useCreatePortfolioState();

    useEffect(() => {
        (async () => {
            if (isPk) {
                try {
                    const params = { language };

                    const responseMP = await SM.portfolioManagement(
                        'getModelPortfolios',
                        [
                            selectedProductSelector?.id,
                            params,
                        ],
                    );

                    const model = adaptModels(responseMP.data)[0];
                    const riskCategory = adaptStrategies(responseMP.data)[0];

                    const payload = {
                        strategyId: riskCategory?.Id,
                        recommendedStrategyId: currentInvestmentApplicationSelector?.recommendedStrategy?.RiskCategory?.Id ?? null,
                        investmentHorizon: INVESTMENT_HORIZON_FOR_PK,
                        modelPortfolioId: model?.Id,
                        withdrawalOption: false,
                    };

                    const additionalData = {
                        riskCategory,
                        modelPortfolio: model,
                    };

                    await saveInvestmentApplicationDataAction({
                        payload,
                        method: 'saveStrategyData',
                        additionalData,
                    });

                    if (currentInvestmentApplicationId) {
                        await SM.customInvestmentService('saveProductData', [{ investmentApplicationId: currentInvestmentApplicationId, initialInvestment: currentInvestmentApplicationSelector?.investmentDescription?.productConfiguration?.initialInvestment }]);
                    }
                } catch (error) {
                    setPageErrors({
                        message: error?.message || t('general.somethingWentWrong'),
                    });
                    notification.open({ content: t('general.somethingWentWrong'), type: 'error' });
                } finally {
                    setIsPreloadInProgress(false);
                }
            } else {
                setIsPreloadInProgress(false);
            }
        })();
    }, [language, isPk, currentInvestmentApplicationId, currentInvestmentApplicationSelector?.recommendedStrategy?.RiskCategory?.Id]);

    const newPortfolio = {
        product: selectedProductSelector,
        applicationData: currentInvestmentApplicationSelector,
        modelPortfolio: currentInvestmentApplicationSelector.modelPortfolio,
    };
    const {
        t,
        isManualPBRestricted,
        isAdvisorCanBeSelected,
        formatPhoneNumber,
        productDesc,
        issueCommissionSelected,
        issueCommissionChange,
        issueCommissionOptions,
        pbSelected,
        pbChange,
        advisorData,
        isValid,
        validation,
        setContinuesValidation,
        changeVSTNumber,
        vstNumber,
        isLoading,
        generalAgencies,
        selectedGeneralAgency,
        setSelectedGeneralAgency,
        agentsList,
        selectedAgent,
        setSelectedAgent,
        handleSearchAgents,
        searchAgentsInProgress,
    } = useBaseAgentInformation({
        dfsClientId,
        newPortfolio,
    });

    const onContinue = useCallback(async () => {
        sessionStorage.removeItem(STRATEGY_CHANGED);
        setContinuesValidation(true);
        if (isValid) {
            setContinueInProgress(true);
            try {
                const payload = {
                    issueCommission: issueCommissionSelected || '0',
                    pbNumber: String(pbSelected),
                    vstNumber,
                    agencyId: selectedGeneralAgency,
                    agentId: selectedAgent,
                };

                let feeOverview;

                if (saveFeeData && !isPk) {
                    feeOverview = await saveFeeData();
                }
                const result = await saveInvestmentApplicationDataAction({
                    payload,
                    method: 'saveAgentData',
                    additionalData: {
                        agentInformation: {
                            pbSelected,
                            issueCommissionSelected,
                            vstNumber,
                            selectedGeneralAgency,
                            selectedAgent,
                        },
                        feeOverview,
                    },
                });

                if (!result) return;
                setStep({
                    stepKey: is3bProductGroupSelector ? 'ShoppingCard' : 'Non3BShoppingCard',
                });
            } catch (err) {
                setPageErrors(err.message);
            }
        }
    }, [
        dfsClientId, pbSelected, vstNumber,
        issueCommissionSelected, isValid, selectedGeneralAgency, selectedAgent, saveFeeData, isPk,
    ]);

    const { setStep } = usePortfolioProcessNavigation({
        saveOnContinue: onContinue,
        enableAutoStepComplete: false,
        navigationDisabled: isLoading,
        continueInProgress,
        continueDisabled: continueInProgress,
        pageErrors,
    });

    return {
        t,
        isManualPBRestricted,
        isAdvisorCanBeSelected,
        isPreloadInProgress,
        validation,
        formatPhoneNumber,
        productDesc,
        issueCommissionSelected,
        issueCommissionChange,
        issueCommissionOptions,
        pbSelected,
        pbChange,
        advisorData,
        changeVSTNumber,
        vstNumber,
        generalAgencies,
        selectedGeneralAgency,
        setSelectedGeneralAgency,
        agentsList,
        selectedAgent,
        setSelectedAgent,
        handleSearchAgents,
        searchAgentsInProgress,
    };
};
